import { lazy, Suspense } from 'react';
import { Route } from 'react-router';
import FloatingLoadingSpinner from '../../components/ui/FloatingLoadingSpinner';
const AdminLayout = lazy(async () => ({ default: (await import('./AdminLayout')).AdminLayout }));
import { modLoader } from './mod.loader';
import { RootErrorBoundary } from '../../components/RootErrorBoundary';
import { routes } from '../../routes/route-definitions';
const ModIndexPage = lazy(async () => ({
    default: (await import('./index/ModIndexPage')).ModIndexPage,
}));
import { levelLoader } from '../level/level.loader';
import type { QueryClient } from '@tanstack/react-query';
const EditPack = lazy(() => import('./edit-pack/EditPackPage').then((module) => ({ default: module.EditPackPage })));
const ManageUser = lazy(() =>
    import('./manage-user/ManageUserPage').then((module) => ({ default: module.ManageUserPage })),
);
const ManageUserContent = lazy(() => import('./manage-user/components/ManageUserContent'));
const Verification = lazy(() =>
    import('./verification/VerificationPage').then((module) => ({ default: module.VerificationPage })),
);
const EditLevel = lazy(() =>
    import('./edit-level/EditLevelPage').then((module) => ({ default: module.EditLevelPage })),
);
const EditTags = lazy(() => import('./edit-tags/EditTagsPage').then((module) => ({ default: module.EditTagsPage })));
const Debugging = lazy(() => import('./debugging/DebuggingPage').then((module) => ({ default: module.DebuggingPage })));
const Roles = lazy(() => import('./roles/Roles').then((module) => ({ default: module.Roles })));
const SubmissionMerge = lazy(() =>
    import('./merge-submissions/SubmissionMergePage').then((module) => ({ default: module.SubmissionMergePage })),
);
const AuditLogs = lazy(() =>
    import('./audit-logs/AuditLogsPage').then((module) => ({ default: module.AuditLogsPage })),
);
const EditRolePage = lazy(() => import('./roles/EditRolePage').then((module) => ({ default: module.EditRolePage })));
const SubmissionSearch = lazy(() =>
    import('./submission-search/SubmissionSearchPage').then((module) => ({ default: module.SubmissionSearchPage })),
);
const Achievements = lazy(async () => ({
    default: (await import('./achievements/AchievementsPage')).AchievementsPage,
}));
const EditCrossroadsPackPage = lazy(async () => ({
    default: (await import('./packs/crossroads-pack/CrossroadsPackPage')).CrossroadsPackPage,
}));
const SparkyPackAdminPage = lazy(async () => ({
    default: (await import('./packs/sparky-pack/SparkyPackAdminPage')).SparkyPackAdminPage,
}));
const GddlpPackAdminPage = lazy(async () => ({
    default: (await import('./packs/gddlp-pack/GddlpPackAdminPage')).GddlpPackAdminPage,
}));
const Queue = lazy(async () => ({ default: (await import('./queue/QueuePage')).QueuePage }));
const Beta = lazy(async () => ({ default: (await import('./beta/BetaAccessPage')).BetaAccessPage }));
const EditReferences = lazy(async () => ({
    default: (await import('./references/EditReferencesPage')).EditReferencesPage,
}));
const SignupLink = lazy(async () => ({
    default: (await import('./signup-links/SignupLinkPage')).SignupLinkPage,
}));
const AddSubmission = lazy(async () => ({
    default: (await import('./add-submissions/AddSubmissionPage')).AddSubmissionPage,
}));
const EditSubmission = lazy(async () => ({
    default: (await import('./edit-submission/EditSubmissionPage')).EditSubmissionPage,
}));
const CreateUser = lazy(async () => ({
    default: (await import('./create-user/CreateUserPage')).CreateUserPage,
}));
const AddLevel = lazy(async () => ({
    default: (await import('./add-level/AddLevelPage')).AddLevelPage,
}));
const DeleteUser = lazy(async () => ({
    default: (await import('./delete-user/DeleteUserPage')).DeleteUserPage,
}));
const SiteSettings = lazy(async () => ({
    default: (await import('./site-settings/SiteSettingsPage')).SiteSettingsPage,
}));

export const staffRoutes = (queryClient: QueryClient) => (
    <Route
        path='/mod'
        element={
            <Suspense fallback={<FloatingLoadingSpinner />}>
                <AdminLayout />
            </Suspense>
        }
        loader={modLoader}
        ErrorBoundary={RootErrorBoundary}
    >
        <Route index Component={ModIndexPage} />
        <Route path='audit-logs' Component={AuditLogs} />
        <Route path='beta' Component={Beta} />
        <Route path='queue' Component={Queue} />
        <Route path='references' Component={EditReferences} />
        <Route path={routes.staff.editPack.path} Component={EditPack} />
        <Route path={routes.staff.editPack.withId.path} Component={EditPack} />
        <Route path={routes.staff.achievements.path} Component={Achievements} />
        <Route path={routes.staff.crossroadsPack.path} Component={EditCrossroadsPackPage} />
        <Route path={routes.staff.sparkyPack.path} Component={SparkyPackAdminPage} />
        <Route path={routes.staff.addSubmission.path} Component={AddSubmission} />
        <Route path={routes.staff.gddlpPack.path} Component={GddlpPackAdminPage} />
        <Route path={routes.staff.editSubmission.path} Component={EditSubmission} />
        <Route path={routes.staff.manageUser.path} Component={ManageUser}>
            <Route path={routes.staff.manageUser.withId.path} Component={ManageUserContent} />
        </Route>
        <Route path={routes.staff.createUser.path} Component={CreateUser} />
        <Route path={routes.staff.deleteUser.path} Component={DeleteUser} />
        <Route path={routes.staff.signupLinks.path} Component={SignupLink} />
        <Route path='verification' Component={Verification} />
        <Route path={routes.staff.addLevel.path} Component={AddLevel} />
        <Route path='edit-level/:levelID' Component={EditLevel} loader={levelLoader(queryClient)} />
        <Route path={routes.staff.editTags.path} Component={EditTags} />
        <Route path={routes.staff.siteSettings.path} Component={SiteSettings} />
        <Route path='debugging' Component={Debugging} />
        <Route path='roles' Component={Roles} />
        <Route path='roles/:roleID' Component={EditRolePage} />
        <Route path={routes.staff.mergeSubmissions.path} Component={SubmissionMerge} />
        <Route path={routes.staff.submissions.search.path} Component={SubmissionSearch} />
    </Route>
);
